import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { 
  LayoutDashboard, 
  Users, 
  Wrench, 
  ClipboardList, 
  History, 
  Bell, 
  UserCog, 
  LogOut,
  Key,
  AlertCircle,
  FileDown,
  Boxes,
  Menu
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
} from '@/components/ui/avatar';

const Layout = () => {
  const { user, logout } = React.useContext(AuthContext);
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Build menu based on role
  let menuItems = [];
  
  if (user?.role === 'client') {
    menuItems = [
      { path: '/rentals', icon: Key, label: 'Location' },
    ];
  } else if (user?.role === 'collaborator') {
    menuItems = [
      { path: '/', icon: LayoutDashboard, label: 'Mes Outils' },
      { path: '/rentals', icon: Key, label: 'Location' },
    ];
  } else {
    menuItems = [
      { path: '/', icon: LayoutDashboard, label: 'Tableau de bord' },
      { path: '/collaborators', icon: Users, label: 'Collaborateurs' },
      { path: '/tools', icon: Wrench, label: 'Outils' },
      { path: '/kits', icon: Boxes, label: 'Kits' },
      { path: '/rentals', icon: Key, label: 'Location' },
      { path: '/tool-issues', icon: AlertCircle, label: 'Signalements' },
      { path: '/history', icon: History, label: 'Historique' },
      { path: '/alerts', icon: Bell, label: 'Alertes' },
      { path: '/export', icon: FileDown, label: 'Export/Import' },
    ];

    if (user?.role === 'admin') {
      menuItems.push({ path: '/users', icon: UserCog, label: 'Utilisateurs' });
    }
  }

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Admin',
      manager: 'Gestionnaire',
      collaborator: 'Collaborateur',
      client: 'Client'
    };
    return labels[role] || role;
  };

  const SidebarContent = ({ onNavigate }) => (
    <>
      <div className="p-6 border-b border-border">
        <h1 className="text-2xl font-bold font-heading tracking-tight bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          ToolTracker
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Gestion d'inventaire</p>
      </div>

      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} onClick={onNavigate}>
              <div
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium text-sm">{item.label}</span>
              </div>
            </Link>
          );
        })}
      </nav>

      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 mb-3">
          <Avatar>
            <AvatarFallback className="bg-primary text-primary-foreground">
              {user?.name?.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground capitalize">{getRoleLabel(user?.role)}</p>
          </div>
        </div>
        <Button
          data-testid="logout-button"
          onClick={() => { if (onNavigate) onNavigate(); logout(); }}
          variant="outline"
          className="w-full rounded-full"
          size="sm"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop Sidebar — hidden on mobile */}
      <aside className="hidden md:flex w-64 bg-card border-r border-border flex-col shrink-0">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <div className="flex-1 overflow-auto flex flex-col min-w-0">
        {/* Mobile top bar with hamburger */}
        <div className="md:hidden sticky top-0 z-30 flex items-center gap-3 border-b border-border bg-card/80 backdrop-blur px-4 py-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                data-testid="mobile-menu-trigger"
                variant="ghost"
                size="icon"
                className="rounded-full"
              >
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 flex flex-col">
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <h1 className="text-lg font-bold font-heading tracking-tight bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            ToolTracker
          </h1>
        </div>

        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default Layout;
